import React, { createContext, useContext, useRef, useCallback, useState } from 'react';

/**
 * ExecutionContext tracks active node executions via Map<nodeId, AbortController>.
 * Each execution can be cancelled by calling abort() on its controller.
 */

interface ExecutionContextType {
  // Register an execution for a node
  registerExecution: (nodeId: string, abortController: AbortController) => void;
  // Unregister when execution completes
  unregisterExecution: (nodeId: string) => void;
  // Stop execution for a specific node
  stopExecution: (nodeId: string) => void;
  // Check if a node has an active execution
  hasActiveExecution: (nodeId: string) => boolean;
  // Get set of currently executing node IDs
  executingNodeIds: Set<string>;
}

const ExecutionContext = createContext<ExecutionContextType | null>(null);

export function ExecutionProvider({ children }: { children: React.ReactNode }) {
  const executionsRef = useRef<Map<string, AbortController>>(new Map());
  const [executingNodeIds, setExecutingNodeIds] = useState<Set<string>>(new Set());

  const registerExecution = useCallback((nodeId: string, abortController: AbortController) => {
    executionsRef.current.set(nodeId, abortController);
    setExecutingNodeIds(prev => new Set([...prev, nodeId]));
  }, []);

  const unregisterExecution = useCallback((nodeId: string) => {
    executionsRef.current.delete(nodeId);
    setExecutingNodeIds(prev => {
      const next = new Set(prev);
      next.delete(nodeId);
      return next;
    });
  }, []);

  const stopExecution = useCallback((nodeId: string) => {
    const controller = executionsRef.current.get(nodeId);
    if (controller) {
      controller.abort();
    }
  }, []);

  const hasActiveExecution = useCallback((nodeId: string) => {
    return executionsRef.current.has(nodeId);
  }, []);

  return (
    <ExecutionContext.Provider
      value={{
        registerExecution,
        unregisterExecution,
        stopExecution,
        hasActiveExecution,
        executingNodeIds,
      }}
    >
      {children}
    </ExecutionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useExecution() {
  const context = useContext(ExecutionContext);
  if (!context) {
    throw new Error('useExecution must be used within ExecutionProvider');
  }
  return context;
}
